/**
 * Preferred Locale API Client (User)
 *
 * Read/update the authenticated user's server-persisted language preference.
 * The update path is best-effort (errors swallowed) so a failed sync never
 * disrupts the local language switch already applied via i18next + localStorage.
 */

import { axiosInstance } from '@/shared/lib/axios';
import { logger } from '@/shared/utils/logger';

/** Get the user's preferred locale, or null if unavailable. */
export const getUserPreferredLocale = async (
  userId: string,
): Promise<string | null> => {
  try {
    const response = await axiosInstance.get<{ preferredLocale: string }>(
      `/api/v1/users/${userId}/preferred-locale`,
    );
    return response.data?.preferredLocale ?? null;
  } catch (error) {
    logger.warn('Failed to fetch user preferred locale', { error });
    return null;
  }
};

/** Persist the user's preferred locale (best-effort, returns null on failure). */
export const updateUserPreferredLocale = async (
  userId: string,
  preferredLocale: string,
): Promise<string | null> => {
  try {
    const response = await axiosInstance.patch<{ preferredLocale: string }>(
      `/api/v1/users/${userId}/preferred-locale`,
      { preferredLocale },
    );
    return response.data?.preferredLocale ?? null;
  } catch (error) {
    logger.warn('Failed to sync user preferred locale', { error });
    return null;
  }
};
